/**
 * API Configuration
 * Handles API URL configuration based on environment
 * Supports both build-time and runtime configuration
 */

import { loadRuntimeConfig, getCachedConfig } from './runtimeConfig';



/**
 * Gets the API base URL from runtime config
 * @returns Promise<string> - The API base URL
 * @throws Error if runtime config cannot be loaded or apiUrl is not configured
 */
export async function getApiBaseUrl(): Promise<string> {
  const runtimeConfig = await loadRuntimeConfig();
  if (!runtimeConfig.apiUrl) {
    throw new Error('API URL not configured in runtime config');
  }
  return runtimeConfig.apiUrl.replace(/\/$/, '');
}

/**
 * Gets the media base URL from runtime config
 * @returns Promise<string> - The media base URL
 * @throws Error if runtime config cannot be loaded or mediaUrl is not configured
 */
export async function getMediaBaseUrl(): Promise<string> {
  const runtimeConfig = await loadRuntimeConfig();
  if (!runtimeConfig.mediaUrl) {
    throw new Error('Media URL not configured in runtime config');
  }
  return runtimeConfig.mediaUrl.replace(/\/$/, '');
}

/**
 * Gets the media base URL synchronously (uses cached runtime config)
 * @returns string - The media base URL
 * @throws Error if runtime config is not cached or mediaUrl is not configured
 */
export function getMediaBaseUrlSync(): string {
  const cachedConfig = getCachedConfig();
  if (!cachedConfig?.mediaUrl) {
    throw new Error('Media URL not configured or runtime config not loaded');
  }
  return cachedConfig.mediaUrl.replace(/\/$/, '');
}

/**
 * Gets the API base URL synchronously (uses cached runtime config)
 * @returns string - The API base URL
 * @throws Error if runtime config is not cached or apiUrl is not configured
 */
export function getApiBaseUrlSync(): string {
  const cachedConfig = getCachedConfig();
  if (!cachedConfig?.apiUrl) {
    throw new Error('API URL not configured or runtime config not loaded');
  }
  return cachedConfig.apiUrl.replace(/\/$/, '');
}

/**
 * Constructs full media URL from endpoint path
 * @param endpoint - Media endpoint path (e.g., '/recipe-images/123/image.jpg')
 * @returns string - Full media URL
 * @throws Error if media URL is not configured
 */
export const getMediaUrl = (endpoint: string): string => {
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  const baseUrl = getMediaBaseUrlSync();
  return `${baseUrl}${normalizedEndpoint}`;
};

/**
 * Async version of getMediaUrl that waits for runtime config
 * @param endpoint - Media endpoint path (e.g., '/recipe-images/123/image.jpg')
 * @returns Promise<string> - Full media URL
 * @throws Error if media URL is not configured
 */
export const getMediaUrlAsync = async (endpoint: string): Promise<string> => {
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  const baseUrl = await getMediaBaseUrl();
  return `${baseUrl}${normalizedEndpoint}`;
};

/**
 * Constructs full API URL from endpoint path
 * @param endpoint - API endpoint path (e.g., '/recipes')
 * @returns string - Full API URL
 * @throws Error if API URL is not configured
 */
export const getApiUrl = (endpoint: string): string => {
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  const baseUrl = getApiBaseUrlSync();
  return `${baseUrl}${normalizedEndpoint}`;
};

/**
 * Async version of getApiUrl that waits for runtime config
 * @param endpoint - API endpoint path (e.g., '/recipes')
 * @returns Promise<string> - Full API URL
 * @throws Error if API URL is not configured
 */
export const getApiUrlAsync = async (endpoint: string): Promise<string> => {
  // Ensure endpoint starts with /
  const normalizedEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  const baseUrl = await getApiBaseUrl();
  return `${baseUrl}${normalizedEndpoint}`;
};

/**
 * Configuration object for API and media settings
 */
export const apiConfig = {
  getBaseUrl: getApiBaseUrl,
  getBaseUrlSync: getApiBaseUrlSync,
  getUrl: getApiUrl,
  getUrlAsync: getApiUrlAsync,
  // Media configuration
  getMediaBaseUrl: getMediaBaseUrl,
  getMediaBaseUrlSync: getMediaBaseUrlSync,
  getMediaUrl: getMediaUrl,
  getMediaUrlAsync: getMediaUrlAsync,
  // Environment-specific settings
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
} as const;